import { apiClient, request } from '@shared/api/client';
import { toBackendRole } from '../lib/constants';
import { isInvitableRole } from '../lib/invite-roles';
import type { Member, Scope, InvitePayload } from './schemas';
import {
  MemberListResponseSchema,
  InviteListResponseSchema,
  InviteTokenResponseSchema,
  AcceptInviteResponseSchema,
  JoinRequestListResponseSchema,
  toMember,
  toInvite,
  toInvitePayload,
  toJoinRequest,
  type Invite,
  type JoinRequest,
  type AcceptInviteResponse,
} from './schemas';

export async function listMembers(orgId: string): Promise<Member[]> {
  const raw = await apiClient.get<unknown>(`/organization/${orgId}/members`);
  const parsed = MemberListResponseSchema.parse(raw);
  return parsed.data.map(toMember);
}

/* ---------- member management ---------- */

export async function updateMemberScope(
  orgId: string,
  userId: string,
  scope: Scope | null
): Promise<void> {
  await apiClient.patch(`/organization/${orgId}/members/${userId}/scope`, { scope });
}

export async function updateMemberStatus(
  orgId: string,
  userId: string,
  status: 'active' | 'suspended'
): Promise<void> {
  await apiClient.patch(`/organization/${orgId}/members/${userId}/status`, { status });
}

export async function updateMemberRole(orgId: string, userId: string, role: string): Promise<void> {
  await apiClient.patch(`/organization/${orgId}/members/${userId}/role`, {
    role: toBackendRole(role),
  });
}

export async function updateMemberTeam(
  orgId: string,
  userId: string,
  teamId: string | null
): Promise<void> {
  await apiClient.patch(`/organization/${orgId}/members/${userId}/team`, { teamId });
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  await apiClient.delete(`/organization/${orgId}/members/${userId}`);
}

export async function forceSignout(orgId: string, userId: string): Promise<void> {
  await apiClient.post(`/organization/${orgId}/members/${userId}/force-signout`);
}

export async function transferOwnership(orgId: string, targetUserId: string): Promise<void> {
  await apiClient.post(`/organization/${orgId}/members/${targetUserId}/transfer-ownership`, {
    targetUserId,
  });
}

/* ---------- invitations ---------- */

export interface InviteMemberPayload {
  email: string;
  role: string;
  teamId?: string | null;
  scope?: Scope | null;
}

export async function inviteMember(orgId: string, body: InviteMemberPayload): Promise<void> {
  if (!isInvitableRole(body.role)) {
    throw new Error(`Unsupported invitation role: ${body.role}`);
  }
  await apiClient.post(`/organization/${orgId}/members/invite`, {
    email: body.email,
    role: toBackendRole(body.role),
    teamId: body.teamId ?? null,
    scope: body.scope ? JSON.stringify(body.scope) : undefined,
  });
}

export async function listInvites(orgId: string): Promise<Invite[]> {
  const raw = await apiClient.get<unknown>(`/organization/${orgId}/invites`);
  const parsed = InviteListResponseSchema.parse(raw);
  return parsed.data.map(toInvite);
}

export async function resendInvite(orgId: string, inviteId: string): Promise<void> {
  await apiClient.post(`/organization/${orgId}/invites/${inviteId}/resend`);
}

export async function revokeInvite(orgId: string, inviteId: string): Promise<void> {
  await apiClient.delete(`/organization/${orgId}/invites/${inviteId}`);
}

/**
 * Look up an invitation by its token (public, unauthenticated).
 *
 * `GET /organization/invitations/:token` — backend returns 200 with invite
 * details, 404 if the token is unknown / already used, or 400 if expired.
 */
export async function getInviteByToken(token: string): Promise<InvitePayload> {
  const raw = await request<unknown>(`/organization/invitations/${encodeURIComponent(token)}`, {
    method: 'GET',
    auth: false,
  });
  const parsed = InviteTokenResponseSchema.parse(raw);
  return toInvitePayload(parsed.data);
}

/* ---------- accept invitation ---------- */

export async function acceptInvite(token: string): Promise<AcceptInviteResponse> {
  const raw = await request<unknown>('/organization/invitations/accept', {
    method: 'POST',
    json: { token },
  });
  return AcceptInviteResponseSchema.parse(raw);
}

/* ---------- shareable invite link ---------- */

export interface CreateShareableLinkPayload {
  role: string;
  teamId?: string;
  scope?: string;
  expiryHours?: number;
  maxUses?: number;
  emailDomainRestriction?: string;
  requiresApproval?: boolean;
}

export async function createShareableLink(
  orgId: string,
  payload: CreateShareableLinkPayload
): Promise<{ invitationId: string; token: string; url: string }> {
  const raw = await apiClient.post<unknown>(`/organization/${orgId}/invites/link`, {
    ...payload,
    role: toBackendRole(payload.role),
  });
  return (raw as { data: { invitationId: string; token: string; url: string } }).data;
}

/* ---------- join requests ---------- */

export async function listJoinRequests(orgId: string): Promise<JoinRequest[]> {
  const raw = await apiClient.get<unknown>(`/organization/${orgId}/requests`);
  const parsed = JoinRequestListResponseSchema.parse(raw);
  // The queue acts on pending requests only; the backend returns all statuses.
  return parsed.data.requests.filter((request) => request.status === 'pending').map(toJoinRequest);
}

export async function resolveJoinRequest(
  orgId: string,
  requestId: string,
  approved: boolean,
  role?: string
): Promise<void> {
  await apiClient.patch(`/organization/${orgId}/requests/${requestId}`, {
    action: approved ? 'approve' : 'reject',
    ...(approved && role ? { role: toBackendRole(role) } : {}),
  });
}
